'use client'

import { FunctionComponent, useContext, useMemo, useCallback, useState, useEffect, MouseEventHandler } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '../Button/Button'
import { CartListContext } from '../CartList/CartListContext'
import { CartContext } from '../../context/CartContext'
import { getAreAllItemsStillValidOnCart } from '../CheckoutWorkflow/getIsItemStillValidOnCart'
import { useTranslation, ConfigContext } from '../../context/ConfigContext'
import useCurrentLocale from '../../../core/hook/useCurrentLocale'
import { tapbuyRedirectDocument } from '../../../queries/TapbuyRedirect.gql'
import { useQuery } from '../../../core/hook/useQuery'
import { useTapbuyPixel } from '@tapbuy-public/pixel-tracker'
import { ModalContext } from '../../context/ModalContext'

const CartResumeButton: FunctionComponent = () => {
  const currentLocale = useCurrentLocale()
  const router = useRouter()
  const { cart, modalUuid } = useContext(CartContext)
  const { updatingProducts } = useContext(CartListContext)
  const { payloadAppConfig } = useContext(ConfigContext)
  const { firePixel, getCookies } = useTapbuyPixel({
    cookies: {
      keys: [
        '_ga*', // Wildcard, gets all _ga cookies
        'optanon*',
        'cookie-consent',
        '_fbp',
        '_pinterest_ct_ua',
        'tb-abtest-id',
      ],
    },
  })
  const t = useTranslation()
  const { removeModal } = useContext(ModalContext)!

  const areAllItemsStillValidOnCart = useMemo(() => getAreAllItemsStillValidOnCart(cart), [cart])

  const [triggerQuery, setTriggerQuery] = useState(false)

  const { data, loading, error } = useQuery(tapbuyRedirectDocument, {
    variables: {
      cartId: cart?.id,
      cookies: JSON.stringify(getCookies()),
      referer: typeof window !== 'undefined' ? window.location.href : '',
    },
    context: { headers: { credentials: 'include' } },
    fetchPolicy: 'network-only',
  })


  const disabled = loading || !!updatingProducts.length || !areAllItemsStillValidOnCart

  const handleClick = useCallback<MouseEventHandler>((ev) => {
    ev.preventDefault();
    if (!cart?.id) {
      router.push(`/${currentLocale}/checkout`)
      removeModal(modalUuid)
      return
    }
    if (!payloadAppConfig.flags?.enableTapbuy) {
      // Force native checkout: bypass query completely
      router.push(`/${currentLocale}/checkout`)
      removeModal(modalUuid)
      return
    }
    setTriggerQuery(true)
  }, [cart?.id, currentLocale, router, payloadAppConfig.flags?.enableTapbuy])

  useEffect(() => {
    if (!triggerQuery) return
    if (loading) return
    if (error) {
      router.push(`/${currentLocale}/checkout`)
      return
    }
    const resp = data?.tapbuyRedirect
    if (resp) {
      firePixel(resp.pixel_url || '')
    }
    if (resp?.redirect && resp.redirect_url) {
      window.location.href = resp.redirect_url;
    } else {
      router.push(`/${currentLocale}/checkout`)
      removeModal(modalUuid)
    }
  }, [triggerQuery, loading, error, data, currentLocale, router])

  return (
    <div className='flex flex-col gap-2'>
      <Button
        isLoading={loading}
        label={t('Go to Checkout', {}, false)}
        disabled={disabled}
        onClick={(ev) => handleClick(ev)}
        classname='black w-full font-bold'
        size='large'
      />
      {!areAllItemsStillValidOnCart && (
        <div className='text-red-700'>{t('checkout-error-stock')}</div>
      )}
    </div>
  )
}

export default CartResumeButton
